import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Space, Tag, Typography, Row, Col, Select, Modal, Form, Upload, message } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined, UploadOutlined, DownloadOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { deviceService } from '../../services/deviceService';
import { Device, DeviceRequest, DeviceType, DeviceBatch as DeviceBatchData } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import DealerSelector from '../../components/DealerSelector';
import BatchSelector from '../../components/BatchSelector';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const DeviceList: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Device | null>(null);
  const [dataSource, setDataSource] = useState<Device[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [deviceBatches, setDeviceBatches] = useState<DeviceBatchData[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<any>({});
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();
  
  // 批次筛选相关状态
  const [batchFilterValue, setBatchFilterValue] = useState<number | undefined>();
  const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(() => {
    // 如果不是超级经销商，默认使用当前用户的经销商ID
    return user?.dealerType === 1 ? undefined : user?.dealerId;
  });

  // 加载设备类型和批次
  useEffect(() => {
    loadDeviceTypes();
  }, []);

  // 初始化经销商ID（非超级经销商默认使用自己的经销商ID）
  useEffect(() => {
    if (user && user.dealerType !== 1 && user.dealerId) {
      setSelectedDealerId(user.dealerId);
    }
  }, [user]);

  // 加载数据
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [pagination.current, pagination.pageSize, searchParams, user]);

  const loadDeviceTypes = async () => {
    try {
      const response = await deviceService.getAllDeviceTypes();
      if (response.data) {
        setDeviceTypes(response.data);
      }
    } catch (error) {
      console.error('加载设备类型失败:', error);
    }
  };

  const loadDeviceBatches = async (typeId?: number) => {
    if (!user) return;

    try {
      const response = await deviceService.getDeviceBatchList({
        adminUserId: user.userId,
        deviceTypeId: typeId,
        pageNo: 1,
        pageSize: 100,
      });

      if (response.data) {
        setDeviceBatches(response.data.content || []);
      }
    } catch (error) {
      console.error('加载设备批次失败:', error);
    }
  };

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await deviceService.getDeviceList({
        adminUserId: user.userId,
        pageNo: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams,
      });

      if (response.data) {
        setDataSource(response.data.content || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.totalElements || 0,
        }));
      }
    } catch (error) {
      console.error('加载设备列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<Device> = [
    {
      title: '设备ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '设备编号',
      dataIndex: 'deviceNum',
      key: 'deviceNum',
      width: 200,
    },
    {
      title: '设备类型',
      dataIndex: 'typeName',
      key: 'typeName',
    },
    {
      title: '设备厂商标识',
      dataIndex: 'firm',
      key: 'firm',
    },
    {
      title: '批次名称',
      dataIndex: 'batchName',
      key: 'batchName',
    },
    {
      title: '经销商',
      dataIndex: 'dealerName',
      key: 'dealerName',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    // {
    //   title: '操作',
    //   key: 'action',
    //   width: 250,
    //   fixed: 'right',
    //   render: (_, record: Device) => (
    //     <Space size="small">
    //       <Button
    //         type="link"
    //         size="small"
    //         icon={<EditOutlined />}
    //         onClick={() => handleEdit(record)}
    //       >
    //         编辑
    //       </Button>
    //       <Button
    //         type="link"
    //         size="small"
    //         danger
    //         icon={<DeleteOutlined />}
    //         onClick={() => handleDelete(record)}
    //         disabled={record.deleted}
    //       >
    //         删除
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  const handleSearch = (value: string) => {
    setSearchParams((prev: any) => ({ ...prev, deviceNum: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setSearchParams((prev: any) => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));

    // 当设备类型变化时，重新加载批次列表
    if (key === 'typeId') {
      loadDeviceBatches(value);
      form.setFieldValue('batchId', undefined);
    }
  };

  // 处理批次筛选变化
  const handleBatchFilterChange = (batchId: number) => {
    setBatchFilterValue(batchId);
    
    // 更新搜索参数
    const newSearchParams: any = { ...searchParams };
    
    if (batchId) {
      newSearchParams.batchId = batchId;
      newSearchParams.dealerId = selectedDealerId;
    } else {
      // 如果清空了批次选择，也清空相关的筛选参数
      delete newSearchParams.batchId;
      delete newSearchParams.dealerId;
    }
    
    setSearchParams(newSearchParams);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 处理经销商筛选变化（用于与批次筛选器联动）
  const handleDealerFilterChange = (dealerId: number) => {
    setSelectedDealerId(dealerId);
    
    // 如果当前有批次选择，则清空批次选择（因为经销商变了）
    if (batchFilterValue) {
      setBatchFilterValue(undefined);
      const newSearchParams = { ...searchParams };
      delete newSearchParams.batchId;
      newSearchParams.dealerId = dealerId;
      setSearchParams(newSearchParams);
    } else {
      // 否则正常更新经销商筛选
      handleFilterChange('dealerId', dealerId);
    }
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 清空所有筛选条件
  const handleClearAllFilters = () => {
    setBatchFilterValue(undefined);
    // 如果是超级经销商，清空经销商选择；如果是普通经销商，保持自己的经销商ID
    if (user?.dealerType === 1) {
      setSelectedDealerId(undefined);
    } else {
      setSelectedDealerId(user?.dealerId);
    }
    setSearchParams({});
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: Device) => {
    setEditingRecord(record);
    form.setFieldsValue({
      typeId: record.typeId,
      batchId: record.batchId,
      deviceNum: record.deviceNum,
    });
    // 加载对应类型的批次
    loadDeviceBatches(record.typeId);
    setIsModalVisible(true);
  };

  const handleDelete = async (record: Device) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除设备"${record.deviceNum}"吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deviceService.deleteDevice(record.id);
          // 只有当后端返回成功状态时才显示成功消息
          if (response.status === 1) {
            message.success('删除成功');
          }
          // 无论成功与否都刷新数据，因为删除可能已经生效
          loadData();
        } catch (error) {
          // 这里只处理网络错误等异常情况
          console.error('删除失败:', error);
        }
      },
    });
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的设备');
      return;
    }

    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个设备吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deviceService.batchDeleteDevices(selectedRowKeys as number[]);
          // 只有当后端返回成功状态时才显示成功消息
          if (response.status === 1) {
            message.success('批量删除成功');
          }
          setSelectedRowKeys([]);
          // 无论成功与否都刷新数据，因为删除可能已经生效
          loadData();
        } catch (error) {
          // 这里只处理网络错误等异常情况
          console.error('批量删除失败:', error);
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const data: DeviceRequest = {
        typeId: values.typeId,
        batchId: values.batchId,
        deviceNum: values.deviceNum,
      };

      if (editingRecord) {
        await deviceService.updateDevice(editingRecord.id, data);
        message.success('修改成功');
      } else {
        await deviceService.createDevice(data);
        message.success('新增成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      console.error('操作失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleImport = () => {
    importForm.resetFields();
    setIsImportModalVisible(true);
  };

  const handleImportOk = async () => {
    try {
      const values = await importForm.validateFields();
      const { file, typeId, batchId } = values;

      if (!file || !file.file) {
        message.error('请选择要导入的文件');
        return;
      }

      await deviceService.importDevices(file.file.originFileObj, typeId, batchId);
      message.success('导入成功');
      setIsImportModalVisible(false);
      importForm.resetFields();
      loadData();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      console.error('导入失败:', error);
    }
  };

  const handleImportCancel = () => {
    setIsImportModalVisible(false);
    importForm.resetFields();
  };

  const handleExport = async () => {
    if (!user) return;

    try {
      const blob = await deviceService.exportDevices({
        adminUserId: user.userId,
        ...searchParams,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `设备列表_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const uploadProps: UploadProps = {
    maxCount: 1,
    beforeUpload: () => false, // 阻止自动上传
    accept: '.xls,.xlsx',
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>设备列表</Title>
          </Col>
          <Col>
            <Space>
              {/* <Button icon={<PlusOutlined />} type="primary" onClick={handleAdd}>
                新增设备
              </Button>
              <Button icon={<UploadOutlined />} onClick={handleImport}>
                导入设备
              </Button> */}
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
              <Button onClick={handleClearAllFilters}>
                清空筛选
              </Button>
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Search
                placeholder="搜索设备编号"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: 300 }}
                onSearch={handleSearch}
              />
              <Select 
                placeholder="设备类型" 
                style={{ width: 150 }} 
                allowClear
                onChange={(value) => handleFilterChange('typeId', value)}
              >
                {deviceTypes.map(type => (
                  <Option key={type.id} value={type.id}>{type.name}</Option>
                ))}
              </Select>
             
              {/* 超级经销商显示经销商筛选器 */}
              {user?.dealerType === 1 && (
                <DealerSelector
                  placeholder="筛选经销商"
                  style={{ width: 250 }}
                  allowClear
                  value={searchParams.dealerId}
                  onChange={handleDealerFilterChange}
                  status={1}
                />
              )}
              
              {/* 批次筛选器 */}
              <BatchSelector
                placeholder="筛选批次"
                style={{ width: 330 }}
                allowClear
                value={batchFilterValue}
                onChange={handleBatchFilterChange}
                dealerId={selectedDealerId}
              />
            </Space>
          </Col>
          {/* <Col>
            <Space>
              <Button onClick={handleBatchDelete} disabled={selectedRowKeys.length === 0}>
                批量删除
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                导出数据
              </Button>
            </Space>
          </Col> */}
        </Row>

        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          rowSelection={rowSelection}
          scroll={{ x: 1200 }}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>

      {/* 新增/编辑设备模态框 */}
      <Modal
        title={editingRecord ? '编辑设备' : '新增设备'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="确定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="typeId"
            label="设备类型"
            rules={[{ required: true, message: '请选择设备类型' }]}
          >
            <Select 
              placeholder="请选择设备类型"
              onChange={(value) => {
                loadDeviceBatches(value);
                form.setFieldValue('batchId', undefined);
              }}
            >
              {deviceTypes.map(type => (
                <Option key={type.id} value={type.id}>{type.name} ({type.firm})</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="batchId"
            label="设备批次"
            rules={[{ required: true, message: '请选择设备批次' }]}
          >
            <Select placeholder="请先选择设备类型">
              {deviceBatches.map(batch => (
                <Option key={batch.id} value={batch.id}>{batch.batchName}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="deviceNum"
            label="设备编号"
            rules={[{ required: true, message: '请输入设备编号' }]}
          >
            <Input placeholder="请输入设备编号（同一类型下必须唯一）" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Excel导入模态框 */}
      <Modal
        title="导入设备"
        open={isImportModalVisible}
        onOk={handleImportOk}
        onCancel={handleImportCancel}
        okText="导入"
        cancelText="取消"
      >
        <Form form={importForm} layout="vertical">
          <Form.Item
            name="typeId"
            label="设备类型"
            rules={[{ required: true, message: '请选择设备类型' }]}
          >
            <Select 
              placeholder="请选择设备类型"
              onChange={(value) => {
                loadDeviceBatches(value);
                importForm.setFieldValue('batchId', undefined);
              }}
            >
              {deviceTypes.map(type => (
                <Option key={type.id} value={type.id}>{type.name} ({type.firm})</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="batchId"
            label="设备批次"
            rules={[{ required: true, message: '请选择设备批次' }]}
          >
            <Select placeholder="请先选择设备类型">
              {deviceBatches.map(batch => (
                <Option key={batch.id} value={batch.id}>{batch.batchName}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="file"
            label="Excel文件"
            rules={[{ required: true, message: '请选择要导入的Excel文件' }]}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
          <div style={{ color: '#999', fontSize: 12, marginTop: -16 }}>
            <p>说明：</p>
            <ul>
              <li>支持 .xls 和 .xlsx 格式</li>
              <li>Excel 第一列为设备编号</li>
              <li>第一行为标题行（会被跳过）</li>
            </ul>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceList;
